import React, { useEffect, useState } from "react";

import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import { BlockStandard } from "components/admin/BlockStandard";
import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { useUserContext } from "contexts/UserContext";
import { ButtonLink } from "components/MUIExtensions/ButtonLink";
import {
  CREATE_AC,
  CREATE_BUILDING,
  CREATE_ENVIRONMENT,
} from "routers/routePaths";
import { AutomationSpace } from "api/automationTypes";
import { fetchACList, fetchEnvironmentStatus } from "libLegacy/api/dashboard";
import { DeviceAC, Environment } from "libLegacy/api/models";
import { useSetError } from "contexts/ErrorContext";
import { useDeviceContext } from "contexts/DeviceContext";

export const ConfigureIR = () => {
  const { setChosenBuilding, setChosenEnvironment, setChosenAC } =
    useDeviceContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { automationSpaces, token } = useUserContext();
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [acOptions, setAcOptions] = useState<Array<DeviceAC>>([]);
  const setGlobalError = useSetError();

  const [selectedSpace, setSelectedSpace] = useState<
    AutomationSpace | undefined
  >();
  const [selectedEnvironment, setSelectedEnvironment] = useState<
    Environment | undefined
  >();
  const [selectedAC, setSelectedAC] = useState<DeviceAC | undefined>();

  useEffect(() => {
    const loadEnvironments = async () => {
      setIsLoading(true);
      try {
        const data = await fetchEnvironmentStatus(
          token,
          selectedSpace?.id || ""
        );
        setEnvironments(data || []);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedSpace) {
      // clean up selections that depend on this info
      setEnvironments([]);
      setSelectedEnvironment(undefined);

      // load the new info
      loadEnvironments();
    }
  }, [selectedSpace, token, setGlobalError]);

  useEffect(() => {
    const loadACs = async () => {
      setIsLoading(true);
      try {
        const data = await fetchACList(token, selectedSpace?.id || "");
        setAcOptions(data || []);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      }
    };

    if (selectedSpace) {
      loadACs();
    } else {
      // clean up selections that depend on this info
      setAcOptions([]);
    }
  }, [selectedSpace, token, setGlobalError]);

  const handleSpaceChange = ({ target }) => {
    setSelectedSpace(
      automationSpaces.find((space) => space.id === target.value)
    );
  };

  const handleEnvironmentChange = ({ target }) => {
    setSelectedEnvironment(
      environments.find((environment) => environment.name === target.value)
    );
  };

  const handleACChange = ({ target }) => {
    setSelectedAC(acOptions.find((device) => device.name === target.value));
  };

  const handleSubmit = () => {
    if (selectedSpace && selectedEnvironment && selectedAC) {
      setChosenBuilding(selectedSpace);
      setChosenEnvironment(selectedEnvironment);
      setChosenAC(selectedAC);
    }
  };

  return (
    <SolutionsFrame
      title="Configuração de controle remoto universal"
      imgSrc="/static/img/solutions/automate-ac.png"
    >
      <BlockStandard hideDivider>
        <Typography>
          Escolha o imóvel, ambiente e ar-condicionado para o qual deseja
          configurar o seu dispositivo IoT
        </Typography>
      </BlockStandard>

      <BlockStandard hideDivider>
        <FormControl>
          <InputLabel htmlFor="space">Imóvel</InputLabel>
          <Select
            id="space"
            label="Imóvel"
            value={selectedSpace?.id}
            onChange={handleSpaceChange}
          >
            {automationSpaces.map((space) => (
              <MenuItem key={space.id} value={space.id}>
                <Typography>
                  {`${space.brand ? `${space.brand} - ` : ""}${
                    space.schoolName
                  }`}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </BlockStandard>

      {!!selectedSpace && (
        <BlockStandard hideDivider>
          <FormControl>
            <InputLabel htmlFor="building">Ambiente</InputLabel>
            <Select
              id="building"
              label="Ambiente"
              value={selectedEnvironment?.name}
              onChange={handleEnvironmentChange}
            >
              {environments.map((environment) => (
                <MenuItem key={environment.name} value={environment.name}>
                  <Typography>{environment.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </BlockStandard>
      )}

      {!!selectedEnvironment && (
        <BlockStandard hideDivider>
          <InputLabel htmlFor="acSelect">Ar-condicionado</InputLabel>
          <Select
            id="acSelect"
            label="Ar-condicionado"
            value={selectedAC}
            onChange={handleACChange}
          >
            {acOptions
              .filter(
                (option) => option.environment_id === selectedEnvironment.id
              )
              .map((ac) => (
                <MenuItem key={ac.name} value={ac.name}>
                  <Typography>{ac.name}</Typography>
                </MenuItem>
              ))}
          </Select>
        </BlockStandard>
      )}

      <BlockStandard hideDivider>
        <Divider sx={{ width: "100%" }} variant="fullWidth">
          <Typography sx={{ mx: 2 }}> OU </Typography>
        </Divider>

        {!selectedSpace && (
          <ButtonLink to={CREATE_BUILDING} variant="contained">
            {isLoading ? (
              <CircularProgress color="secondary" />
            ) : (
              "Configurar para novo imóvel"
            )}
          </ButtonLink>
        )}

        {!!selectedSpace && !selectedEnvironment && (
          <ButtonLink to={CREATE_ENVIRONMENT} variant="contained">
            {isLoading ? (
              <CircularProgress color="secondary" />
            ) : (
              "Configurar para novo ambiente"
            )}
          </ButtonLink>
        )}

        {!!selectedEnvironment && !selectedAC && (
          <ButtonLink
            to={`${CREATE_AC}/${selectedEnvironment.id}`}
            variant="contained"
          >
            {isLoading ? (
              <CircularProgress color="secondary" />
            ) : (
              "Configurar para novo ar-condicionado"
            )}
          </ButtonLink>
        )}

        {!!selectedAC && (
          <Button variant="contained" onClick={handleSubmit}>
            Continuar
          </Button>
        )}
      </BlockStandard>
    </SolutionsFrame>
  );
};
